import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Skeleton, SkeletonChart, SkeletonStatGrid } from '../components/Skeleton';
import { AlertTriangle, RefreshCw, Download } from 'lucide-react';

const COLORS = ['#3B82F6', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#F472B6'];

const DOW_LABELS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

function Heatmap({ data }) {
  // Build a 7×24 grid keyed (dow,hour) -> count
  const grid = {};
  let max = 0;
  for (const d of data) {
    grid[`${d.dow}-${d.hour}`] = d.count;
    if (d.count > max) max = d.count;
  }
  const colorFor = (count) => {
    if (!count) return 'bg-navy-900/40';
    const intensity = Math.min(1, count / Math.max(max, 1));
    if (intensity > 0.8) return 'bg-accent-blue';
    if (intensity > 0.6) return 'bg-accent-blue/70';
    if (intensity > 0.4) return 'bg-accent-blue/50';
    if (intensity > 0.2) return 'bg-accent-blue/30';
    return 'bg-accent-blue/15';
  };
  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="flex gap-1 ml-10 mb-1">
          {Array.from({ length: 24 }).map((_, h) => (
            <div key={h} className="text-[9px] text-slate-500 w-5 text-center">
              {h % 3 === 0 ? h : ''}
            </div>
          ))}
        </div>
        {DOW_LABELS.map((label, dow) => (
          <div key={dow} className="flex items-center gap-1 mb-1">
            <div className="w-9 text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
            {Array.from({ length: 24 }).map((_, h) => {
              const count = grid[`${dow}-${h}`] || 0;
              return (
                <div
                  key={h}
                  title={`${label} ${h}:00 — ${count} samtaler`}
                  className={`w-5 h-5 rounded-sm ${colorFor(count)} transition-colors hover:ring-2 hover:ring-accent-blue/50 cursor-default`}
                />
              );
            })}
          </div>
        ))}
        <div className="flex items-center gap-2 mt-3 ml-10 text-[10px] text-slate-500">
          <span>Mindre</span>
          <div className="w-4 h-4 rounded-sm bg-navy-900/40" />
          <div className="w-4 h-4 rounded-sm bg-accent-blue/15" />
          <div className="w-4 h-4 rounded-sm bg-accent-blue/30" />
          <div className="w-4 h-4 rounded-sm bg-accent-blue/50" />
          <div className="w-4 h-4 rounded-sm bg-accent-blue/70" />
          <div className="w-4 h-4 rounded-sm bg-accent-blue" />
          <span>Mer</span>
        </div>
      </div>
    </div>
  );
}

function Funnel({ total, appointments, callbacks, textbacks }) {
  const engaged = (appointments || 0) + (callbacks || 0) + (textbacks || 0);
  const stages = [
    { label: 'Mottatte samtaler', count: total, color: 'bg-slate-600' },
    { label: 'Engasjerte (handling tatt)', count: engaged, color: 'bg-accent-blue' },
    { label: 'Tilbakeringinger + SMS', count: (callbacks || 0) + (textbacks || 0), color: 'bg-accent-green' },
    { label: 'Booket avtale', count: appointments, color: 'bg-purple-400' },
  ];
  const max = Math.max(1, total);
  return (
    <div className="space-y-3">
      {stages.map((s, i) => {
        const pct = max > 0 ? Math.round((s.count / max) * 100) : 0;
        const conversionFromTop = total > 0 ? Math.round((s.count / total) * 100) : 0;
        return (
          <div key={i}>
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>{s.label}</span>
              <span className="font-mono">{s.count} • {conversionFromTop}%</span>
            </div>
            <div className="h-7 bg-navy-900/60 rounded-md overflow-hidden">
              <div
                className={`h-full ${s.color} transition-all duration-500 flex items-center px-3 text-xs font-medium text-white`}
                style={{ width: `${Math.max(pct, 2)}%` }}
              >
                {pct >= 12 && <span>{pct}%</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function exportAnalyticsCSV(data, days) {
  if (!data) return;
  // Build rows from daily_breakdown if available, otherwise single summary row
  const rows = [['date', 'total_calls', 'appointments', 'callbacks', 'avg_duration', 'avg_lead_score']];
  if (data.daily_breakdown && data.daily_breakdown.length > 0) {
    data.daily_breakdown.forEach(row => {
      rows.push([
        row.date || row.range || '',
        row.count ?? row.total_calls ?? '',
        row.appointments ?? '',
        row.callbacks ?? '',
        row.avg_duration ?? data.avg_duration ?? '',
        row.avg_lead_score ?? data.avg_lead_score ?? '',
      ]);
    });
  } else {
    rows.push([
      new Date().toISOString().slice(0, 10),
      data.total_calls ?? '',
      data.appointments_booked ?? '',
      (data.outcome_breakdown || []).find(o => o.outcome === 'callback')?.count ?? '',
      data.avg_duration ?? '',
      data.avg_lead_score ?? '',
    ]);
  }
  const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `analyse-${days}d-${new Date().toISOString().slice(0,10)}.csv`);
  document.body.appendChild(link); link.click(); link.remove();
  window.URL.revokeObjectURL(url);
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get(`/api/analytics?days=${days}`);
      setData(res);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || 'Kunne ikke laste analysedata. Sjekk tilkoblingen og prøv igjen.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="analytics-loading">
        <Skeleton height={28} width={180} />
        <SkeletonStatGrid count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonChart height={280} />
          <SkeletonChart height={280} />
          <SkeletonChart height={280} />
          <SkeletonChart height={280} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="analytics-error" className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md text-center bg-navy-800 border border-navy-600 rounded-lg p-8">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-red/15 flex items-center justify-center">
            <AlertTriangle size={22} className="text-accent-red" />
          </div>
          <h2 className="font-heading text-lg font-semibold text-white mb-2">Kunne ikke laste analyse</h2>
          <p className="text-sm text-slate-400 mb-5">{error || 'Ukjent feil'}</p>
          <button
            onClick={load}
            data-testid="analytics-retry"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors"
          >
            <RefreshCw size={14} /> Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  const tooltipStyle = { backgroundColor: '#121927', border: '1px solid #222F43', borderRadius: '8px', color: '#F8FAFC' };

  return (
    <div data-testid="analytics-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Analyse</h1>
          <p className="text-slate-500 text-sm mt-1">Innsikt og trender for dine samtaler</p>
        </div>
        <div className="flex gap-2 items-center">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              data-testid={`period-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                days === d ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'
              }`}
            >
              {d === 7 ? '7 dager' : d === 30 ? '30 dager' : '90 dager'}
            </button>
          ))}
          <button
            onClick={() => exportAnalyticsCSV(data, days)}
            disabled={!data}
            data-testid="analytics-export-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue hover:text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={14} />
            Eksporter rapport
          </button>
        </div>
      </div>

      {/* Empty state when no calls in selected period */}
      {(data.total_calls || 0) === 0 ? (
        <div data-testid="analytics-empty" className="bg-navy-800 border border-navy-600 rounded-lg p-12 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-blue/15 flex items-center justify-center">
            <Download size={20} className="text-accent-blue" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-white mb-2">Ingen samtaler i denne perioden</h3>
          <p className="text-sm text-slate-400 max-w-md mx-auto">Når AI-resepsjonisten din begynner å motta samtaler, vil grafer, trender og innsikt vises her. Prøv et lengre tidsrom eller test agenten din fra dashbordet.</p>
        </div>
      ) : null}

      {/* Summary row */}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 ${(data.total_calls || 0) === 0 ? 'opacity-50' : ''}`}>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-2xl font-heading font-bold text-white">{data.total_calls}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Samtaler</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-2xl font-heading font-bold text-accent-green">{data.hot_leads}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Varme leads</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-2xl font-heading font-bold text-purple-400">{data.appointments_booked}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Avtaler</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-2xl font-heading font-bold text-accent-yellow">
            {data.avg_duration ? `${Math.floor(data.avg_duration / 60)}m` : '0m'}
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Snitt varighet</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calls over time */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-4">Samtaler over tid</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.calls_by_day}>
                <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} tickFormatter={d => d.split('-').slice(1).join('/')} axisLine={{ stroke: '#222F43' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Intent breakdown */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-4">Type henvendelser</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.intent_breakdown}
                  dataKey="count"
                  nameKey="intent"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  strokeWidth={0}
                >
                  {data.intent_breakdown.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  verticalAlign="bottom"
                  formatter={(value) => <span className="text-xs text-slate-400">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lead score distribution */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-4">Lead score fordeling</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.lead_score_distribution}>
                <XAxis dataKey="range" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={{ stroke: '#222F43' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.lead_score_distribution.map((entry, i) => (
                    <Cell key={i} fill={i === 4 ? '#34D399' : i === 3 ? '#3B82F6' : '#222F43'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Busiest hours */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-4">Travleste timer</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.hourly_distribution}>
                <XAxis dataKey="hour" tick={{ fill: '#64748B', fontSize: 10 }} tickFormatter={h => `${h}:00`} axisLine={{ stroke: '#222F43' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={h => `Kl. ${h}:00`} />
                <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Outcome distribution */}
        {data.outcome_breakdown && data.outcome_breakdown.some(o => o.count > 0) && (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 lg:col-span-2">
            <h3 className="font-heading text-base font-semibold text-white mb-4">Resultatfordeling</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.outcome_breakdown.filter(o => o.count > 0)}
                      dataKey="count"
                      nameKey="outcome"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={55}
                      strokeWidth={0}
                      label={(e) => `${Math.round((e.count / data.total_calls) * 100)}%`}
                      labelLine={false}
                    >
                      {data.outcome_breakdown.filter(o => o.count > 0).map((entry, i) => {
                        const color = entry.key === 'appointment' ? '#A78BFA'
                          : entry.key === 'callback' ? '#3B82F6'
                          : entry.key === 'textback' ? '#34D399'
                          : '#475569';
                        return <Cell key={i} fill={color} />;
                      })}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, p) => [`${v} samtaler`, p.payload.outcome]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {data.outcome_breakdown.map((o, i) => {
                  const pct = data.total_calls > 0 ? Math.round((o.count / data.total_calls) * 100) : 0;
                  const color = o.key === 'appointment' ? 'bg-purple-400'
                    : o.key === 'callback' ? 'bg-accent-blue'
                    : o.key === 'textback' ? 'bg-accent-green'
                    : 'bg-slate-600';
                  return (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-3 h-3 rounded-sm ${color}`} />
                        <span className="text-sm text-slate-300">{o.outcome}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 font-mono">{o.count}</span>
                        <span className="text-sm font-mono text-white w-10 text-right">{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Peak hours heatmap (weekday × hour) */}
        {data.weekday_hour_heatmap && (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 lg:col-span-2">
            <h3 className="font-heading text-base font-semibold text-white mb-1">Travleste tider (uke × time)</h3>
            <p className="text-xs text-slate-500 mb-4">Mørkere = flere samtaler. Hjelper deg planlegge bemanning.</p>
            <Heatmap data={data.weekday_hour_heatmap} />
          </div>
        )}

        {/* Conversion funnel: Calls → Engaged → Callback → Appointment */}
        {data.outcome_breakdown && data.total_calls > 0 && (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 lg:col-span-2">
            <h3 className="font-heading text-base font-semibold text-white mb-1">Konverteringstrakt</h3>
            <p className="text-xs text-slate-500 mb-5">Fra mottatte samtaler til booket avtale.</p>
            <Funnel
              total={data.total_calls}
              appointments={data.appointments_booked || 0}
              callbacks={data.callbacks || 0}
              textbacks={data.textbacks_sent || 0}
            />
          </div>
        )}

        {/* Avg response time + Top callers */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-1">Gjennomsnittlig svartid</h3>
          <p className="text-xs text-slate-500 mb-6">Hvor raskt AI-en tar samtalen.</p>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-heading font-bold text-accent-blue">
              {data.avg_response_seconds ? `${Math.round(data.avg_response_seconds * 10) / 10}s` : '< 1s'}
            </span>
            <span className="text-xs text-slate-500">snitt over perioden</span>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Snitt varighet</div>
              <div className="text-lg font-heading font-bold text-white mt-1">
                {data.avg_duration ? `${Math.floor(data.avg_duration / 60)}m ${data.avg_duration % 60}s` : '0m'}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Total samtaler</div>
              <div className="text-lg font-heading font-bold text-white mt-1">{data.total_calls}</div>
            </div>
          </div>
        </div>

        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-1">Hyppigste innringere</h3>
          <p className="text-xs text-slate-500 mb-4">Top 10 numre i perioden.</p>
          {data.top_callers && data.top_callers.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.top_callers.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-navy-700/40 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{c.caller || c.from_number || 'Ukjent'}</div>
                    {c.from_number && c.caller !== c.from_number && (
                      <div className="text-xs text-slate-500 font-mono">{c.from_number}</div>
                    )}
                  </div>
                  <div className="text-right ml-3">
                    <div className="text-sm font-mono text-white">{c.count}</div>
                    <div className="text-xs text-slate-500">samtaler</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-slate-500">Ingen data å vise ennå</div>
          )}
        </div>

        {/* Sentiment */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-white mb-4">Stemningsanalyse</h3>
          <div className="flex items-center gap-8 justify-center py-8">
            {data.sentiment_breakdown.map((s, i) => {
              const color = s.sentiment === 'Positive' ? '#34D399' : s.sentiment === 'Negative' ? '#F87171' : '#94A3B8';
              const pct = data.total_calls > 0 ? Math.round((s.count / data.total_calls) * 100) : 0;
              return (
                <div key={i} className="text-center">
                  <div className="text-4xl font-heading font-bold" style={{ color }}>{pct}%</div>
                  <div className="text-sm text-slate-400 mt-2">
                    {s.sentiment === 'Positive' ? 'Positiv' : s.sentiment === 'Negative' ? 'Negativ' : 'Nøytral'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.count} samtaler</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
